import React, { useCallback } from 'react';
import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useAppDispatch } from 'store/hooks';
import { zoomPositionChanged } from 'features/content-zoom/contentZoomSlice';

interface ZoomableContextProps {
  children: React.ReactNode;
}

export const ZoomableContext: React.FC<ZoomableContextProps> = ({ children }) => {
  const dispatch = useAppDispatch();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 10,
      },
    })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, delta } = event;

      if (String(active.id).startsWith('zoom-') && delta) {
        const data = active.data.current;
        if (data?.zoomId) {
          const scale = data.scale || 1;
          const normalizedDeltaX = delta.x / scale;
          const normalizedDeltaY = delta.y / scale;

          const newOffsetX = data.currentOffsetX + normalizedDeltaX;
          const newOffsetY = data.currentOffsetY + normalizedDeltaY;

          const shouldConstrain =
            data.constrainPosition && data.isPositionOutOfBounds && data.maxDragDistance !== 0;

          if (shouldConstrain) {
            const constrainedPosition = data.constrainPosition(newOffsetX, newOffsetY);
            dispatch(
              zoomPositionChanged({
                zoomId: data.zoomId,
                offsetX: constrainedPosition.offsetX,
                offsetY: constrainedPosition.offsetY,
              })
            );
          } else {
            dispatch(
              zoomPositionChanged({
                zoomId: data.zoomId,
                offsetX: newOffsetX,
                offsetY: newOffsetY,
              })
            );
          }
        }
      }
    },
    [dispatch]
  );

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      {children}
    </DndContext>
  );
};
